import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useI18n } from '../i18n/i18nContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { packageAPI, userAPI } from '../services/api';
import { useAuth0 } from '@auth0/auth0-react';
import { getTranslatedField } from '../utils/translationUtils';
import { UserProfile, Package, Promotion } from '@shared/types';
import { pushToDataLayer, clearEcommerce, formatPackageItem } from '../utils/dataLayer';
import { formatPrice } from '@shared/utils';
import DiscountCode, {
  AppliedGiftCard,
  AppliedPromo,
} from 'src/components/CheckoutPage/DiscountCode';
import CheckoutForm from 'src/components/CheckoutPage/CheckoutForm';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const CheckoutPage: React.FC = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const { t, language } = useI18n();
  const { user, isAuthenticated } = useAuth0();
  const [packageDetails, setPackageDetails] = useState<Package | null>(null);
  const [promotionDetails, setPromotionDetails] = useState<Promotion | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [appliedGiftCard, setAppliedGiftCard] = useState<AppliedGiftCard | null>(null);
  const [appliedPromotion, setAppliedPromotion] = useState<AppliedPromo | null>(null);

  const [initialPackageIds, setInitialPackageIds] = useState<string[]>([]);
  const [initialSubIds, setInitialSubIds] = useState<string[]>([]);
  const [baselineLoaded, setBaselineLoaded] = useState(false);
  const [promotionDiscountPercentage, setPromotionDiscountPercentage] = useState<number>(0);

  useDocumentTitle(t.checkout.title);

  useEffect(() => {
    fetchPackageDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserProfile();
      fetchBaseline();
    } else if (!isAuthenticated) {
      setBaselineLoaded(true);
    }
  }, [isAuthenticated, user]);

  const hasTrackedBeginCheckout = useRef(false);
  useEffect(() => {
    if (packageDetails && !hasTrackedBeginCheckout.current) {
      hasTrackedBeginCheckout.current = true;
      clearEcommerce();
      const category = packageDetails.type === 'subscription' ? 'subscription' : 'package';
      const itemType = packageDetails.type === 'subscription' ? 'Subscription' : 'Package';

      pushToDataLayer('begin_checkout', {
        content: { category },
        ecommerce: {
          items: [formatPackageItem(packageDetails, itemType)],
        },
      });
    }
  }, [packageDetails]);

  useEffect(() => {
    if (promotionDetails && packageDetails) {
      if (packageDetails.type === 'subscription' && promotionDetails.subscription_discount_pct) {
        setPromotionDiscountPercentage(promotionDetails.subscription_discount_pct);
      } else if (packageDetails.type === 'bulk' && promotionDetails.package_discount_pct) {
        setPromotionDiscountPercentage(promotionDetails.package_discount_pct);
      }
    }
  }, [promotionDetails, packageDetails]);

  const fetchPackageDetails = async (code?: string) => {
    try {
      const packagesResponse = await packageAPI.getAll(code);

      const responseData = (packagesResponse.data as any)?.data || packagesResponse.data;
      const allPackages = Array.isArray(responseData?.packages)
        ? responseData.packages
        : Array.isArray(responseData)
          ? responseData
          : [];

      if (!Array.isArray(allPackages)) {
        throw new Error('Invalid packages data format');
      }

      const selectedPackage = allPackages.find((pkg: Package) => pkg.id === packageId);

      if (!selectedPackage) {
        throw new Error(t.checkout.packageNotFound);
      }

      setPackageDetails(selectedPackage);
      setPromotionDetails(responseData?.promotionDetails);
    } catch (err: any) {
      setError(err.message || t.checkout.loadError);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await userAPI.getProfile();
      setUserProfile(response.data);
    } catch (err: any) {
      console.error('Failed to fetch user profile:', err);
    }
  };

  const fetchBaseline = async () => {
    try {
      const [pkgsRes, subsRes] = await Promise.all([
        userAPI.getUserPackages().catch(() => ({ data: [] })),
        userAPI.getSubscriptions().catch(() => ({ data: [] })),
      ]);

      const getIds = (res: any) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        return list.map((item: any) => item.id);
      };

      setInitialPackageIds(getIds(pkgsRes));
      setInitialSubIds(getIds(subsRes));
    } catch (err) {
      console.warn('Failed to fetch baseline for polling', err);
    } finally {
      setBaselineLoaded(true);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">{t.common.loading}</div>
      </div>
    );
  }

  if (error || !packageDetails) {
    return (
      <div className="container mx-auto max-w-4xl p-4 md:p-8">
        <div className="py-10 text-center">
          <div className="mb-4 text-red-600">{error || t.checkout.loadError}</div>
          <button onClick={() => window.history.back()} className="text-blue-600 hover:underline">
            {t.common.goBack}
          </button>
        </div>
      </div>
    );
  }

  const discountedPrice = packageDetails.price;

  let giftCardAmountToApply = 0;
  if (appliedGiftCard) {
    giftCardAmountToApply = Math.min(appliedGiftCard.balance, discountedPrice);
  }

  const finalAmount = Math.max(0, discountedPrice - giftCardAmountToApply);

  return (
    <div className="container mx-auto max-w-4xl p-4 md:p-8">
      <h1 className="mb-8 text-center text-3xl font-bold text-gray-800 md:text-4xl">
        {t.checkout.title}
      </h1>

      <div className="grid gap-8 md:grid-cols-2">
        <div className="rounded-lg bg-white p-6 shadow-lg">
          <h2 className="mb-4 text-xl font-bold">{t.checkout.orderSummary}</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold">
                {getTranslatedField(packageDetails, 'name', language)}
              </h3>
              <p className="text-gray-600">
                {packageDetails.type === 'subscription'
                  ? t.checkout.monthlySubscription
                  : t.checkout.bulkPackage}
              </p>
              <p className="text-gray-600">
                {packageDetails.sessions} {t.checkout.sessions}
              </p>
            </div>

            <div className="border-t pt-4">
              {packageDetails.originalPrice &&
              packageDetails.originalPrice > packageDetails.price &&
              promotionDiscountPercentage > 0 ? (
                <>
                  <div className="mb-2 flex justify-between">
                    <span>{t.checkout.subtotal}</span>
                    <span>{formatPrice(packageDetails.originalPrice)}</span>
                  </div>

                  <div className="mb-2 flex justify-between text-green-600">
                    <span>
                      {promotionDiscountPercentage}%{' '}
                      {(appliedPromotion
                        ? t.checkout.promotionCodeDiscount
                        : t.checkout.promotionDiscount) || 'Promotion Discount'}
                    </span>
                    <span>-{formatPrice(packageDetails.originalPrice - packageDetails.price)}</span>
                  </div>
                </>
              ) : null}

              {appliedGiftCard && (
                <div className="mb-2 flex justify-between text-green-600">
                  <span>{t.checkout.giftCardDiscount}</span>
                  <span>-{formatPrice(giftCardAmountToApply)}</span>
                </div>
              )}

              <div className="flex justify-between text-lg font-bold">
                <span>{t.checkout.total}</span>
                <span>{formatPrice(finalAmount)}</span>
              </div>
            </div>

            <DiscountCode
              type="giftCard"
              setDiscount={setAppliedGiftCard}
              discount={appliedGiftCard}
            />
            <DiscountCode
              type="promo"
              setDiscount={setAppliedPromotion}
              fetchPackageDetails={fetchPackageDetails}
              discount={appliedPromotion}
            />
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-lg">
          <div className="mb-2 flex items-center">
            <svg
              className="mr-2 h-5 w-5 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
            <h2 className="text-xl font-bold">{t.checkout.paymentDetails}</h2>
          </div>
          <p className="mb-4 text-sm text-gray-500">{t.checkout.securePaymentInfo}</p>

          <Elements stripe={stripePromise}>
            {baselineLoaded ? (
              <CheckoutForm
                packageDetails={packageDetails}
                giftCardBalance={giftCardAmountToApply}
                finalAmount={finalAmount}
                user={user}
                userProfile={userProfile}
                appliedGiftCard={appliedGiftCard}
                promotion={promotionDetails}
                initialPackageIds={initialPackageIds}
                initialSubIds={initialSubIds}
              />
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500">{t.common.loading}</div>
              </div>
            )}
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
